import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import InstructorForm from './InstructorForm';
import './Admin.css';
import { useTranslation } from 'react-i18next';

const InstructorManagement = () => {
  const { t, i18n } = useTranslation();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const formRef = useRef(null);

  // Force re-render on language change
  useEffect(() => {
    // This empty effect depends on i18n.language
    // and will trigger a re-render when language changes
  }, [i18n.language]);

  // Fetch all instructors
  const fetchInstructors = async () => {
    try {
      setLoading(true);
      let url = 'instructors';
      if (showDeleted) {
        url = 'instructors?include_deleted=true';
      } else if (showInactive) {
        url = 'instructors?include_inactive=true';
      }
      const response = await axios.get(url);
      setInstructors(response.data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching instructors:', err);
      setError(t('admin.loadingInstructors'));
    } finally {
      setLoading(false);
    }
  };

  // Load instructors when component mounts or when filters change
  useEffect(() => {
    fetchInstructors();
  }, [showInactive, showDeleted]);
  
  // Scroll to form when it appears
  useEffect(() => {
    if (showForm && formRef.current) {
      // Add a slight delay to ensure DOM is updated
      setTimeout(() => {
        formRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 100);
    }
  }, [showForm]);

  // Handle form submission (create or update)
  const handleSubmit = async (instructorData) => {
    try {
      setLoading(true);
      
      // Check if instructorData is a response object from an API call
      if (instructorData.data !== undefined && instructorData.status !== undefined) {
        // It's an axios response object - just refresh the list
        console.log('Received API response directly from form:', instructorData.status);
        fetchInstructors();
        setShowForm(false);
        setSelectedInstructor(null);
        return;
      }
      
      // Check if it's an update or create operation
      if (selectedInstructor) {
        // Update existing instructor
        console.log('Updating instructor with ID:', selectedInstructor.id);
        await axios.put(`instructors/${selectedInstructor.id}`, instructorData);
      } else {
        // Create new instructor
        console.log('Creating new instructor');
        await axios.post('instructors', instructorData);
      }
      
      // Refresh the instructor list
      fetchInstructors();
      setShowForm(false);
      setSelectedInstructor(null);
    } catch (err) {
      console.error('Error saving instructor:', err);
      setError(t('admin.failedInstructorSave'));
    } finally {
      setLoading(false);
    }
  };

  // Handle instructor delete (soft delete)
  const handleDelete = async (instructorId) => {
    if (!window.confirm(t('admin.confirmDeactivateInstructor'))) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.delete(`instructors/${instructorId}`);
      fetchInstructors();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error deactivating instructor:', err);
      setError(t('admin.failedDeactivateInstructor'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle instructor reactivation
  const handleReactivate = async (instructorId) => {
    if (!window.confirm(t('admin.confirmReactivateInstructor'))) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`instructors/${instructorId}/reactivate`);
      fetchInstructors();
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error reactivating instructor:', err);
      setError(t('admin.failedReactivateInstructor'));
    } finally {
      setLoading(false);
    }
  };

  // Handle permanent soft-delete (sets status to 'deleted', keeps data in DB)
  const handleSoftDelete = async (instructorId) => {
    if (!window.confirm(t('admin.confirmDeleteInstructor'))) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(`instructors/${instructorId}/soft-delete`);
      fetchInstructors();
      setError('');
    } catch (err) {
      console.error('Error deleting instructor:', err);
      setError(t('admin.failedDeleteInstructor'));
    } finally {
      setLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowForm(false);
    setSelectedInstructor(null);
  };

  // Handle edit
  const handleEdit = (instructor) => {
    console.log("Editing instructor with ID:", instructor.id);
    // Make sure the instructor object has its ID
    const instructorWithId = {
      ...instructor,
      id: instructor.id
    };
    setSelectedInstructor(instructorWithId);
    setShowForm(true);
  };

  // Handle new instructor
  const handleNewInstructor = () => {
    setSelectedInstructor(null);
    setShowForm(true);
  };
  
  // Toggle showing inactive instructors (resets deleted view)
  const toggleInactiveInstructors = () => {
    setShowDeleted(false);
    setShowInactive(prev => !prev);
  };

  // Toggle showing deleted instructors (resets inactive view)
  const toggleDeletedInstructors = () => {
    setShowInactive(false);
    setShowDeleted(prev => !prev);
  };

  return (
    <div className="instructor-management">
      <h2>{t('admin.instructorManagement')}</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="admin-controls">
        <div className="control-options">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={toggleInactiveInstructors}
            />
            {t('admin.showInactiveInstructors')}
          </label>
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={toggleDeletedInstructors}
            />
            {t('admin.showDeletedInstructors')}
          </label>
        </div>
        <button className="add-button" onClick={handleNewInstructor}>
          {t('admin.addNewInstructor')}
        </button>
      </div>
      
      {showForm && (
        <div ref={formRef} className="instructor-form-container">
          <InstructorForm 
            key={`instructor-form-${i18n.language}`}
            initialData={selectedInstructor}
            onSubmit={handleSubmit}
            onCancel={handleFormCancel}
          />
        </div>
      )}
      
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>{t('admin.loadingInstructors')}</p>
        </div>
      ) : instructors.length > 0 ? (
        <div className="instructors-table">
          <table>
            <thead>
              <tr>
                <th>{t('admin.instructorImage')}</th>
                <th>{t('admin.instructorName')}</th>
                <th>{t('admin.instructorTitle')}</th>
                <th>{t('admin.instructorSpecialty')}</th>
                {(showInactive || showDeleted) && <th>{t('admin.status')}</th>}
                <th>{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {instructors.map(instructor => (
                <tr
                  key={instructor.id}
                  className={
                    instructor.status === 'deleted'
                      ? 'deleted-row'
                      : instructor.status === 'inactive'
                        ? 'inactive-row'
                        : ''
                  }
                >
                  <td>
                    <img 
                      src={instructor.image_url || '/images/default-instructor.jpg'} 
                      alt={instructor.name} 
                      className="instructor-thumbnail" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default-instructor.jpg";
                      }}
                    />
                  </td>
                  <td>{instructor.name}</td>
                  <td>{instructor.title}</td>
                  <td>{instructor.specialty}</td>
                  {(showInactive || showDeleted) && <td>{instructor.status}</td>}
                  <td className="actions">
                    {instructor.status === 'active' && (
                      <>
                        <button onClick={() => handleEdit(instructor)} className="edit-button">
                          {t('admin.edit')}
                        </button>
                        <button onClick={() => handleDelete(instructor.id)} className="delete-button">
                          {t('admin.deactivate')}
                        </button>
                      </>
                    )}
                    {instructor.status === 'inactive' && (
                      <>
                        <button onClick={() => handleReactivate(instructor.id)} className="reactivate-button">
                          {t('admin.reactivate')}
                        </button>
                        <button onClick={() => handleSoftDelete(instructor.id)} className="soft-delete-button">
                          {t('admin.deleteInstructor')}
                        </button>
                      </>
                    )}
                    {instructor.status === 'deleted' && (
                      <button onClick={() => handleReactivate(instructor.id)} className="reactivate-button">
                        {t('admin.restoreInstructor')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="no-instructors">{t('admin.noInstructorsFound')}</p>
      )}
    </div>
  );
};

export default InstructorManagement; 